import { useMemo, useState, type ReactNode } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuditEvents, useValidateAuditEvent } from '@/api/hooks';
import { ApiError } from '@/api/client';
import type { AuditEvent, AuditEventKind } from '@/api/types';
import { MaterialIcon } from '@/components/MaterialIcon';
import { StatusBadge } from '@/components/StatusBadge';
import { SkeletonRow } from '@/components/SkeletonRow';
import { EmptyState } from '@/components/EmptyState';
import { ErrorCard } from '@/components/ErrorCard';
import { Drawer } from '@/components/Drawer';
import { JsonView } from '@/components/JsonView';
import { useToast } from '@/components/Toast';
import { MotionNumber } from '@/components/MotionNumber';
import { AUDIT_KIND_OPTIONS, getAuditKindMeta } from '@/constants/audit-kinds';
import { formatTimestamp } from '@/utils/format';
import { cn } from '@/utils/classnames';

type LimitOption = 50 | 100 | 200;
const LIMIT_OPTIONS: LimitOption[] = [50, 100, 200];

type OutcomeFilter = 'all' | 'success' | 'failure';

/**
 * Screen 6 — list audit events from `GET /api/v1/audit/events?limit=N` with
 * client-side filtering for kind / outcome / application_id, plus a small
 * `POST /api/v1/audit/events/validate` utility at the bottom.
 *
 * The backend only honours the `limit` query param (clamp 1..200). All other
 * filters operate on the loaded array — the UI labels them "client-side" so
 * operators don't expect them to round-trip.
 */
export function AuditLitePage() {
  const [limit, setLimit] = useState<LimitOption>(50);
  const [kindFilter, setKindFilter] = useState<AuditEventKind | 'all'>('all');
  const [outcomeFilter, setOutcomeFilter] = useState<OutcomeFilter>('all');
  const [appFilter, setAppFilter] = useState('');
  const [drawerEvent, setDrawerEvent] = useState<AuditEvent | null>(null);

  const events = useAuditEvents(limit);

  const filtered = useMemo(() => {
    if (!events.data) return [] as AuditEvent[];
    const needle = appFilter.trim().toLowerCase();
    return events.data.filter((e) => {
      if (kindFilter !== 'all' && e.kind !== kindFilter) return false;
      if (outcomeFilter !== 'all' && e.outcome !== outcomeFilter) return false;
      if (needle && !(e.application_id ?? '').toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [events.data, kindFilter, outcomeFilter, appFilter]);

  return (
    <div className="flex flex-col gap-panel-gap">
      <Header />
      <Toolbar
        limit={limit}
        onLimit={setLimit}
        kindFilter={kindFilter}
        onKindFilter={setKindFilter}
        outcomeFilter={outcomeFilter}
        onOutcomeFilter={setOutcomeFilter}
        appFilter={appFilter}
        onAppFilter={setAppFilter}
        loadedCount={events.data?.length ?? 0}
        filteredCount={filtered.length}
      />

      <ResultArea
        loading={events.isPending}
        error={events.error ?? null}
        events={filtered}
        onSelect={setDrawerEvent}
      />

      <hr className="my-stack-md border-outline-variant" />

      <ValidationUtility />

      <Drawer
        open={drawerEvent !== null}
        onClose={() => setDrawerEvent(null)}
        title="审计事件详情"
        description="审计是敏感运营数据，避免分享含 actor_subject / application_id 的截图。"
      >
        {drawerEvent ? (
          <div className="flex flex-col gap-stack-md">
            <KeyValueGrid event={drawerEvent} />
            <div>
              <h3 className="mb-stack-sm font-label-caps text-label-caps uppercase text-on-surface-variant">
                Raw JSON
              </h3>
              <JsonView value={drawerEvent} />
            </div>
          </div>
        ) : null}
      </Drawer>
    </div>
  );
}

// ─── Header ────────────────────────────────────────────────────────────────

function Header() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, ease: 'easeOut' }}
      className="flex flex-wrap items-end justify-between gap-stack-md"
    >
      <div>
        <h2 className="font-headline-lg text-headline-lg text-on-surface">Audit Lite</h2>
        <p className="mt-unit font-body-sm text-body-sm text-on-surface-variant">
          审计是敏感运营数据，避免分享含 actor_subject / application_id 的截图。
        </p>
      </div>
      <span className="font-label-caps text-label-caps uppercase text-on-surface-variant">
        前端聚合 · 倒序由后端 ORDER BY occurred_at DESC 保证
      </span>
    </motion.div>
  );
}

// ─── Toolbar ───────────────────────────────────────────────────────────────

interface ToolbarProps {
  limit: LimitOption;
  onLimit: (l: LimitOption) => void;
  kindFilter: AuditEventKind | 'all';
  onKindFilter: (k: AuditEventKind | 'all') => void;
  outcomeFilter: OutcomeFilter;
  onOutcomeFilter: (o: OutcomeFilter) => void;
  appFilter: string;
  onAppFilter: (s: string) => void;
  loadedCount: number;
  filteredCount: number;
}

function Toolbar({
  limit,
  onLimit,
  kindFilter,
  onKindFilter,
  outcomeFilter,
  onOutcomeFilter,
  appFilter,
  onAppFilter,
  loadedCount,
  filteredCount,
}: ToolbarProps) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, ease: 'easeOut' }}
      className="flex flex-wrap items-center justify-between gap-stack-md rounded border border-outline-variant bg-surface p-stack-sm"
    >
      <div className="flex flex-wrap items-center gap-stack-md">
        <div className="flex items-center gap-stack-sm">
          <label className="font-label-caps text-label-caps uppercase text-on-surface-variant">
            LIMIT
          </label>
          <select
            value={limit}
            onChange={(e) => onLimit(Number(e.target.value) as LimitOption)}
            className="h-8 rounded border border-outline-variant bg-surface px-stack-sm py-1 font-body-sm text-body-sm text-on-surface focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          >
            {LIMIT_OPTIONS.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
          <span className="font-label-caps text-label-caps uppercase text-outline">server-side</span>
        </div>

        <FilterField label="KIND">
          <select
            value={kindFilter}
            onChange={(e) => onKindFilter(e.target.value as AuditEventKind | 'all')}
            className="h-8 rounded border border-outline-variant bg-surface px-stack-sm py-1 font-body-sm text-body-sm text-on-surface focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          >
            <option value="all">全部</option>
            {AUDIT_KIND_OPTIONS.map((kind) => (
              <option key={kind} value={kind}>
                {getAuditKindMeta(kind).label} · {kind}
              </option>
            ))}
          </select>
        </FilterField>

        <FilterField label="OUTCOME">
          <select
            value={outcomeFilter}
            onChange={(e) => onOutcomeFilter(e.target.value as OutcomeFilter)}
            className="h-8 rounded border border-outline-variant bg-surface px-stack-sm py-1 font-body-sm text-body-sm text-on-surface focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          >
            <option value="all">全部</option>
            <option value="success">success</option>
            <option value="failure">failure</option>
          </select>
        </FilterField>

        <FilterField label="APPLICATION">
          <div className="relative">
            <MaterialIcon
              name="search"
              size={16}
              className="absolute left-2 top-1/2 -translate-y-1/2 text-outline"
            />
            <input
              type="text"
              value={appFilter}
              onChange={(e) => onAppFilter(e.target.value)}
              placeholder="substring 匹配"
              className="h-8 w-56 rounded border border-outline-variant bg-surface pl-8 pr-3 py-1 font-body-sm text-body-sm text-on-surface placeholder-outline focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
        </FilterField>
      </div>

      <span className="font-label-caps text-label-caps uppercase text-on-surface-variant">
        显示 <MotionNumber value={filteredCount} className="text-on-surface" /> / 已加载{' '}
        <MotionNumber value={loadedCount} className="text-on-surface" />
      </span>
    </motion.section>
  );
}

function FilterField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center gap-stack-sm">
      <label className="font-label-caps text-label-caps uppercase text-on-surface-variant">
        {label}
      </label>
      {children}
      <span className="font-label-caps text-label-caps uppercase text-outline">client-side</span>
    </div>
  );
}

// ─── Result table ──────────────────────────────────────────────────────────

function ResultArea({
  loading,
  error,
  events,
  onSelect,
}: {
  loading: boolean;
  error: ApiError | null;
  events: AuditEvent[];
  onSelect: (e: AuditEvent) => void;
}) {
  const navigate = useNavigate();

  if (loading) {
    return (
      <section className="overflow-hidden rounded border border-outline-variant bg-surface">
        <div className="border-b border-outline-variant bg-[#EBECF0] px-container-padding py-stack-sm">
          <span className="font-label-caps text-label-caps uppercase text-on-surface">
            Recent Events
          </span>
        </div>
        <div className="p-container-padding">
          <SkeletonRow count={8} cells={6} />
        </div>
      </section>
    );
  }

  if (error) {
    if (error.status === 403) {
      return (
        <ErrorCard
          title="无权访问审计事件"
          status={403}
          message="需要 platform_admin 角色才能查看 /api/v1/audit/events。"
        />
      );
    }
    if (error.status === 401) {
      return (
        <ErrorCard
          title="会话已失效"
          status={401}
          message="identity_session cookie 不存在或已过期，请重新登录。"
          onRetry={() => navigate('/login', { replace: true })}
        />
      );
    }
    return <ErrorCard title="审计事件请求失败" status={error.status} body={error.body} />;
  }

  if (events.length === 0) {
    return (
      <EmptyState
        icon="check_circle"
        title="暂无符合条件的审计事件"
        description="放宽客户端过滤条件，或调大 limit 拉取更多服务端记录。"
      />
    );
  }

  return (
    <>
      <DesktopTable events={events} onSelect={onSelect} />
      <MobileCardList events={events} onSelect={onSelect} />
    </>
  );
}

function DesktopTable({
  events,
  onSelect,
}: {
  events: AuditEvent[];
  onSelect: (e: AuditEvent) => void;
}) {
  return (
    <section className="hidden overflow-hidden rounded border border-outline-variant bg-surface md:block">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="border-b border-outline-variant bg-[#EBECF0] font-label-caps text-label-caps uppercase text-on-surface">
              <Th>时间</Th>
              <Th>Kind</Th>
              <Th>Actor Subject</Th>
              <Th>Application</Th>
              <Th>Outcome</Th>
              <Th>Reason</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-outline-variant font-body-sm text-body-sm text-on-surface">
            {events.map((event) => {
              const meta = getAuditKindMeta(event.kind);
              const reason = event.reason ?? '';
              return (
                <tr
                  key={event.id}
                  onClick={() => onSelect(event)}
                  className="h-[40px] cursor-pointer transition-colors hover:bg-surface-container-low"
                >
                  <Td className="whitespace-nowrap text-on-surface-variant">
                    <span className="font-code-md text-code-md">
                      {formatTimestamp(event.occurred_at)}
                    </span>
                  </Td>
                  <Td>
                    <span className="inline-flex items-center gap-unit">
                      <span
                        className={cn('h-1.5 w-1.5 rounded-full', meta.dotClass)}
                        aria-hidden
                      />
                      <span className="font-body-sm text-body-sm text-on-surface">
                        {meta.label}
                      </span>
                      <span className="ml-1 font-code-md text-code-md text-on-surface-variant">
                        {event.kind}
                      </span>
                    </span>
                  </Td>
                  <Td>
                    <span
                      className="block max-w-xs truncate font-code-md text-code-md text-on-surface-variant"
                      title={event.actor_subject ?? ''}
                    >
                      {event.actor_subject ?? '—'}
                    </span>
                  </Td>
                  <Td>
                    <span className="font-code-md text-code-md text-on-surface-variant">
                      {event.application_id ?? '—'}
                    </span>
                  </Td>
                  <Td>
                    <OutcomeBadge outcome={event.outcome} />
                  </Td>
                  <Td>
                    <span
                      className="block max-w-[16rem] truncate text-on-surface-variant"
                      title={reason}
                    >
                      {reason || '—'}
                    </span>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function MobileCardList({
  events,
  onSelect,
}: {
  events: AuditEvent[];
  onSelect: (e: AuditEvent) => void;
}) {
  return (
    <ul className="flex flex-col gap-stack-sm md:hidden">
      {events.map((event) => {
        const meta = getAuditKindMeta(event.kind);
        return (
          <li key={event.id}>
            <button
              type="button"
              onClick={() => onSelect(event)}
              className="flex w-full flex-col gap-unit rounded border border-outline-variant bg-surface p-stack-md text-left transition-colors hover:bg-surface-container-low"
            >
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-unit">
                  <span className={cn('h-1.5 w-1.5 rounded-full', meta.dotClass)} aria-hidden />
                  <span className="font-body-sm text-body-sm text-on-surface">{meta.label}</span>
                </span>
                <OutcomeBadge outcome={event.outcome} />
              </div>
              <div className="font-code-md text-code-md text-on-surface-variant">
                {formatTimestamp(event.occurred_at)} · {event.kind}
              </div>
              <div className="font-body-sm text-body-sm text-on-surface-variant">
                actor: {event.actor_subject ?? '—'}
              </div>
              <div className="font-body-sm text-body-sm text-on-surface-variant">
                app: {event.application_id ?? '—'}
              </div>
              {event.reason ? (
                <div className="font-body-sm text-body-sm text-on-surface-variant">
                  reason: {event.reason}
                </div>
              ) : null}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function OutcomeBadge({ outcome }: { outcome: string }) {
  if (outcome === 'success') {
    return (
      <StatusBadge variant="success" dot>
        成功
      </StatusBadge>
    );
  }
  if (outcome === 'failure') {
    return (
      <StatusBadge variant="danger" dot>
        失败
      </StatusBadge>
    );
  }
  return <StatusBadge variant="neutral">{outcome || '—'}</StatusBadge>;
}

function KeyValueGrid({ event }: { event: AuditEvent }) {
  return (
    <dl className="grid grid-cols-[110px_minmax(0,1fr)] gap-y-stack-sm font-body-sm text-body-sm">
      <Dt>id</Dt>
      <Dd mono>{event.id}</Dd>
      <Dt>occurred_at</Dt>
      <Dd mono>{event.occurred_at}</Dd>
      <Dt>kind</Dt>
      <Dd mono>{event.kind}</Dd>
      <Dt>actor_subject</Dt>
      <Dd mono>{event.actor_subject ?? '—'}</Dd>
      <Dt>application_id</Dt>
      <Dd mono>{event.application_id ?? '—'}</Dd>
      <Dt>outcome</Dt>
      <Dd>
        <OutcomeBadge outcome={event.outcome} />
      </Dd>
      <Dt>reason</Dt>
      <Dd>{event.reason ?? '—'}</Dd>
    </dl>
  );
}

function Dt({ children }: { children: ReactNode }) {
  return <dt className="font-label-caps text-label-caps uppercase text-on-surface-variant">{children}</dt>;
}

function Dd({ children, mono }: { children: ReactNode; mono?: boolean }) {
  return (
    <dd className={cn('m-0 break-all text-on-surface', mono ? 'font-code-md text-code-md' : '')}>
      {children}
    </dd>
  );
}

function Th({ children }: { children: ReactNode }) {
  return <th className="p-table-cell-padding font-semibold">{children}</th>;
}

function Td({ children, className }: { children: ReactNode; className?: string }) {
  return <td className={cn('p-table-cell-padding align-middle', className)}>{children}</td>;
}

// ─── AuditEvent validate utility ───────────────────────────────────────────

const VALIDATE_PLACEHOLDER = `{
  "id": "00000000-0000-0000-0000-000000000000",
  "occurred_at": "2026-05-15T12:00:00Z",
  "kind": "login_succeeded",
  "actor_subject": "user:feishu-<union_id>",
  "application_id": null,
  "outcome": "success",
  "reason": null
}
`;

function ValidationUtility() {
  const toast = useToast();
  const validateMut = useValidateAuditEvent();
  const [text, setText] = useState(VALIDATE_PLACEHOLDER);

  function onValidate() {
    let parsed: AuditEvent;
    try {
      parsed = JSON.parse(text) as AuditEvent;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'JSON 解析失败';
      toast.push({ message: `JSON 解析失败：${message}`, variant: 'error' });
      return;
    }
    validateMut.mutate(parsed, {
      onError: (e) =>
        toast.push({
          message: `Validate 失败 (HTTP ${e.status || '—'})`,
          variant: 'error',
        }),
      onSuccess: () =>
        toast.push({ message: '后端可成功反序列化该 AuditEvent', variant: 'success' }),
    });
  }

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, ease: 'easeOut' }}
      className="overflow-hidden rounded border border-outline-variant bg-surface"
    >
      <header className="border-b border-outline-variant bg-[#EBECF0] px-container-padding py-stack-sm">
        <h3 className="font-label-caps text-label-caps uppercase text-on-surface">
          校验 AuditEvent JSON
        </h3>
      </header>
      <div className="grid grid-cols-1 gap-panel-gap p-container-padding lg:grid-cols-2">
        <div className="flex flex-col gap-stack-sm">
          <p className="m-0 font-body-sm text-body-sm text-on-surface-variant">
            粘贴一份 AuditEvent JSON，调用{' '}
            <code className="font-code-md text-code-md text-on-surface">
              POST /api/v1/audit/events/validate
            </code>{' '}
            检查后端能否反序列化。该接口不写库。
          </p>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            spellCheck={false}
            className="min-h-[220px] w-full resize-y rounded border border-outline-variant bg-surface p-stack-sm font-code-md text-code-md leading-[1.5] text-on-surface placeholder-outline focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            placeholder='{ "id": "...", "occurred_at": "...", "kind": "..." }'
          />
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onValidate}
              disabled={validateMut.isPending}
              className={cn(
                'inline-flex items-center gap-unit rounded bg-primary-container px-stack-md py-stack-sm font-label-caps text-label-caps uppercase text-on-primary',
                'transition-colors hover:bg-primary',
                'disabled:cursor-not-allowed disabled:opacity-70',
              )}
            >
              {validateMut.isPending ? (
                <MaterialIcon name="progress_activity" size={14} className="animate-spin" />
              ) : (
                <MaterialIcon name="fact_check" size={14} />
              )}
              {validateMut.isPending ? 'Validating…' : 'Validate JSON'}
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-stack-sm">
          <h4 className="font-label-caps text-label-caps uppercase text-on-surface-variant">
            服务端回显
          </h4>
          {validateMut.isPending ? (
            <SkeletonRow count={4} cells={2} />
          ) : validateMut.isError ? (
            <ErrorCard
              title="Validate 失败"
              status={validateMut.error.status}
              body={validateMut.error.body}
            />
          ) : validateMut.data ? (
            <JsonView value={validateMut.data} />
          ) : (
            <EmptyState
              icon="fact_check"
              title="粘贴 JSON 后点 Validate"
              description="该接口仅做反序列化校验，不会写入审计表。"
            />
          )}
        </div>
      </div>
    </motion.section>
  );
}
